import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Table } from 'src/design/molecules/Table';
import React from 'react';
import { JobPayCodeRequestDto } from 'src/api/FiretrolApiClient';
import { usePayCodes } from 'src/api/queries/organization/usePayCodes';

export interface IPayCodeOverridesProps {
  value: Omit<JobPayCodeRequestDto, 'jobId'>[];
  onChange: (value: Omit<JobPayCodeRequestDto, 'jobId'>[]) => void;
}

export interface IPayCodeOverride {
  payCodeId: string;
  name: string;
  defaultMultiplier: number;
  rateModifierMultiplier?: number;
}

export const PayCodeOverrides: React.FC<IPayCodeOverridesProps> = ({
  value,
  onChange,
}) => {
  const { data: payCodes, isLoading: loading } = usePayCodes();

  const allPayCodes: IPayCodeOverride[] = React.useMemo(() => {
    return (payCodes ?? []).map((payCode) => {
      const existingOverride = value.find((v) => v.payCodeId === payCode.id);
      return {
        payCodeId: payCode.id,
        name: payCode.name,
        defaultMultiplier: payCode.rateModifierMultiplier,
        rateModifierMultiplier: existingOverride?.rateModifierMultiplier,
      };
    });
  }, [payCodes, value]);

  const updateOverride = (
    payCodeId: string,
    rateModifierMultiplier: number | undefined,
  ) => {
    const existingIndex = value.findIndex((v) => v.payCodeId === payCodeId);

    if (
      rateModifierMultiplier === undefined ||
      rateModifierMultiplier === null
    ) {
      if (existingIndex !== -1) {
        const updated = value.filter((v) => v.payCodeId !== payCodeId);
        onChange(updated);
      }
    } else {
      if (existingIndex !== -1) {
        const updated = value.map((v) =>
          v.payCodeId === payCodeId ? { ...v, rateModifierMultiplier } : v,
        );
        onChange(updated);
      } else {
        const updated = [...value, { payCodeId, rateModifierMultiplier }];
        onChange(updated);
      }
    }
  };

  if (loading) {
    return <Text>Loading pay codes...</Text>;
  }

  if (allPayCodes.length === 0) {
    return (
      <Text c="dimmed">
        No pay codes available. Please create pay codes in Settings first.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm">
        Override rate modifiers for specific pay codes. The multiplier is
        applied to the base hourly rate. Leave blank to use the default
        multiplier.
      </Text>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Pay Code</Table.Th>
            <Table.Th>Default Multiplier</Table.Th>
            <Table.Th>Override Multiplier</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {allPayCodes.map((payCode) => (
            <Table.Tr key={payCode.payCodeId}>
              <Table.Td>{payCode.name}</Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {payCode.defaultMultiplier}x
                </Text>
              </Table.Td>
              <Table.Td>
                <NumberInput
                  value={payCode.rateModifierMultiplier}
                  onChange={(val) =>
                    updateOverride(payCode.payCodeId, val as number | undefined)
                  }
                  placeholder={`Default (${payCode.defaultMultiplier}x)`}
                  decimalScale={2}
                  min={0}
                  max={10}
                  step={0.1}
                  size="xs"
                  description="E.g., 1.5 for time and a half, 2.0 for double time"
                  suffix="x"
                />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};
